import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { usePlan } from "@/context/PlanContext";
import { track } from "@/lib/analytics";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { refresh } = usePlan();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const ref = useRef({ cancelled: false });

  useEffect(() => {
    ref.current.cancelled = false;
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async (n = 0) => {
      if (ref.current.cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setAttempts(n + 1);
        if (data.payment_status === "paid") {
          setStatus("paid");
          track("Pro Upgraded", { revenue: { currency: "USD", amount: 9 } });
          refresh();
          return;
        }
        if (["failed", "expired", "refunded"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (_e) {}
      if (n >= 15) {
        setStatus("timeout");
        return;
      }
      setTimeout(() => poll(n + 1), 2000);
    };
    poll();
    return () => {
      ref.current.cancelled = true;
    };
  }, [sessionId, refresh]);

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-16 fade-up" data-testid="payment-success-page">
      <div className="card-warm p-8 text-center">
        {status === "polling" && (
          <>
            <Loader2 className="animate-spin mx-auto mb-4" size={32} style={{ color: "#2D5A4C" }} />
            <h1 className="font-serif-display text-2xl font-semibold" style={{ color: "#1C2826" }}>
              Confirming your payment…
            </h1>
            <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
              This usually takes just a moment. Attempt {attempts + 1}.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <div
              className="w-16 h-16 rounded-full grid place-items-center mx-auto mb-4"
              style={{ background: "#E8F0EC" }}
            >
              <CheckCircle2 size={32} style={{ color: "#2D5A4C" }} />
            </div>
            <h1 className="font-serif-display text-3xl font-semibold" style={{ color: "#1C2826" }}>
              Welcome to Pro
            </h1>
            <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
              Unlimited analyses, chat, voice, insights, and family profiles are unlocked.
            </p>
            <Button
              onClick={() => navigate("/dashboard")}
              className="btn-brand rounded-full h-11 px-6 mt-6"
              data-testid="payment-success-continue"
            >
              Open dashboard
            </Button>
          </>
        )}
        {(status === "failed" || status === "error") && (
          <>
            <AlertTriangle className="mx-auto mb-4" size={32} style={{ color: "#BF5832" }} />
            <h1 className="font-serif-display text-2xl font-semibold" style={{ color: "#1C2826" }}>
              Payment didn't complete
            </h1>
            <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
              No worries — you can try again anytime.
            </p>
            <Link to="/pricing">
              <Button className="btn-brand rounded-full h-11 px-6 mt-6">Back to pricing</Button>
            </Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <AlertTriangle className="mx-auto mb-4" size={32} style={{ color: "#BF5832" }} />
            <h1 className="font-serif-display text-2xl font-semibold" style={{ color: "#1C2826" }}>
              Still processing
            </h1>
            <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
              Your bank is taking a bit longer. We'll flip you to Pro as soon as Stripe confirms — feel free to refresh.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="btn-brand rounded-full h-11 px-6 mt-6">
              Continue to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
